import React, { Dispatch, SetStateAction } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { evaluateLegendOptional, evaluateLegendOptionalIcon, evaluateLegendRequiredIcon } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { Accordion } from 'react-bootstrap';
import { Contact } from '../../../../models/contact.model';
import { formatBIC, formatIBAN } from '../../../../scripts/format.value.script';

import AppLegend from '../../../../components/element/Legend';
import AppInputImage from '../../../../components/element/InputImage';
import AppInputCountry from '../../../../components/element/InputCountry';
import AppInputCanton from '../../../../components/element/InputCanton';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputLanguage from '../../../../components/element/InputLanguage';
import AppInputQrType from '../../../../components/element/InputQrType';
import iconCard from '../../../../assets/themes/icons/card-secret-icon.svg';
import iconBank from '../../../../assets/themes/icons/bank-ico.svg';

export interface AppContactFormAccordionProps {
  contact: Contact,
  name: {value: string, valid: boolean},
  firstName: {value: string, valid: boolean},
  lastName: {value: string, valid: boolean},
  logo: {value: {name: string, size: number, type: string, view: string, file: any}, valid: {file: boolean, message: string}},
  email: {value: string, valid: boolean},
  phone: {value: string, valid: boolean},
  website: {value: string, valid: boolean},
  registerNumber: {value: string, valid: boolean},
  vatNumber: {value: string, valid: boolean},
  street: {value: string, valid: boolean},
  streetNumber: {value: string, valid: boolean},
  additional: {value: string, valid: boolean},
  administrativeArea: {value: string, valid: boolean},
  city: {value: string, valid: boolean},
  zipCode: {value: string, valid: boolean},
  country: {value: string, valid: boolean},
  currency: {value: string, valid: boolean, object: any},
  financialEntity: {value: string, valid: boolean},
  iban: {value: string, valid: boolean},
  bban: {value: string, valid: boolean},
  bic: {value: string, valid: boolean},
  language: {value: string, valid: boolean},
  qrType: {value: string, valid: boolean},
  setName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setFirstName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setLastName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setLogo: Dispatch<SetStateAction<{value: {name: string, size: number, type: string, view: string, file: any}, valid: {file: boolean, message: string}}>>,
  setEmail: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setPhone: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setWebsite: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setRegisterNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setVatNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreet: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreetNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdditional: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdministrativeArea: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setZipCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCountry: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCurrency: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setFinancialEntity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setIban: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setBban: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setBic: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setLanguage: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setQrType: Dispatch<SetStateAction<{value: string, valid: boolean}>>
};

const AppContactFormAccordion: React.FunctionComponent<AppContactFormAccordionProps> = ({contact, name, firstName, lastName, logo, email, phone, website, registerNumber, vatNumber, street, streetNumber, additional, administrativeArea, city, zipCode, country, currency, financialEntity, iban, bban, bic, language, qrType, setName, setFirstName, setLastName, setLogo, setEmail, setPhone, setWebsite, setRegisterNumber, setVatNumber, setStreet, setStreetNumber, setAdditional, setAdministrativeArea, setCity, setZipCode, setCountry, setCurrency, setFinancialEntity, setIban, setBban, setBic, setLanguage, setQrType}) => {
  const {lang} = StateLanguage()

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeFirstName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFirstName({...firstName, value: event.target.value})
  }

  const handleChangeLastName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setLastName({...lastName, value: event.target.value})
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const handleChangePhone = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPhone({...phone, value: event.target.value})
  }

  const handleChangeWebsite = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setWebsite({...website, value: event.target.value})
  }

  const handleChangeRegisterNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setRegisterNumber({...registerNumber, value: event.target.value})
  }

  const handleChangeVatNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVatNumber({...vatNumber, value: event.target.value})
  }

  const handleChangeStreet = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreet({...street, value: event.target.value})
  }

  const handleChangeStreetNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({...streetNumber, value: event.target.value})
  }

  const handleChangeAdditional = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdditional({...additional, value: event.target.value})
  }

  const handleChangeAdministrativeArea = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdministrativeArea({...administrativeArea, value: event.target.value})
  }

  const handleChangeCity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCity({...city, value: event.target.value})
  }

  const handleChangeZipCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setZipCode({...zipCode, value: event.target.value})
  }

  const handleChangeAccountName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFinancialEntity({...financialEntity, value: event.target.value})
  }

  const handleChangeIban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setIban({...iban, value: event.target.value})
  }

  const handleChangeBban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBban({...bban, value: event.target.value})
  }

  const handleChangeBic = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBic({...bic, value: event.target.value})
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, 'modal-contact-update-input-name', 'modal-contact-update-legend-name-valid', 'modal-contact-update-legend-name-required')
  }

  const validateFirstName = () => {
    evaluateLegendRequiredIcon(expressions.name, firstName, setFirstName, 'modal-contact-update-input-first-name', 'modal-contact-update-legend-first-name-valid', 'modal-contact-update-legend-first-name-required')
  }

  const validateLastName = () => {
    evaluateLegendRequiredIcon(expressions.name, lastName, setLastName, 'modal-contact-update-input-last-name', 'modal-contact-update-legend-last-name-valid', 'modal-contact-update-legend-last-name-required')
  }

  const validateEmail = () => {
    evaluateLegendOptionalIcon(expressions.email, email, setEmail, 'modal-contact-update-input-email', 'modal-contact-update-legend-email-valid')
  }

  const validatePhone = () => {
    evaluateLegendOptionalIcon(expressions.phone, phone, setPhone, 'modal-contact-update-input-phone', 'modal-contact-update-legend-phone-valid')
  }

  const validateWebsite = () => {
    evaluateLegendOptionalIcon(expressions.website, website, setWebsite, 'modal-contact-update-input-website', 'modal-contact-update-legend-website-valid')
  }

  const validateRegisterNumber = () => {
    evaluateLegendOptionalIcon(expressions.registernumber, registerNumber, setRegisterNumber, 'modal-contact-update-input-register-number', 'modal-contact-update-legend-register-number-valid')
  }

  const validateVatNumber = () => {
    evaluateLegendOptionalIcon(expressions.vat, vatNumber, setVatNumber, 'modal-contact-update-input-vat-number', 'modal-contact-update-legend-vat-number-valid')
  }

  const validateStreet = () => {
    evaluateLegendOptionalIcon(expressions.street, street, setStreet, 'modal-contact-update-input-street', 'modal-contact-update-legend-street-valid')
  }

  const validateStreetNumber = () => {
    evaluateLegendOptionalIcon(expressions.streetnumber, streetNumber, setStreetNumber, 'modal-contact-update-input-street-number', 'modal-contact-update-legend-street-number-valid')
  }

  const validateAdditional = () => {
    evaluateLegendOptionalIcon(expressions.text, additional, setAdditional, 'modal-contact-update-input-additional', 'modal-contact-update-legend-additional-valid')
  }

  const validateAdministrativeArea = () => {
    evaluateLegendOptionalIcon(expressions.location, administrativeArea, setAdministrativeArea, 'modal-contact-update-input-administrative-area', 'modal-contact-update-legend-administrative-area-valid')
  }

  const validateCity = () => {
    evaluateLegendOptionalIcon(expressions.location, city, setCity, 'modal-contact-update-input-city', 'modal-contact-update-legend-city-valid')
  }

  const validateZipCode = () => {
    evaluateLegendOptionalIcon(expressions.zipcode, zipCode, setZipCode, 'modal-contact-update-input-zip-code', 'modal-contact-update-legend-zip-code-valid')
  }

  const validateFinancialEntity = () => {
    evaluateLegendOptional(expressions.name, financialEntity, setFinancialEntity, 'modal-contact-update-legend-financial-entity-valid')
  }

  const validateIban = () => {
    evaluateLegendOptional(expressions.iban, iban, setIban, 'modal-contact-update-legend-iban-valid')
  }

  const validateBban = () => {
    evaluateLegendOptional(expressions.bban, bban, setBban, 'modal-contact-update-legend-bban-valid')
  }

  const validateBic = () => {
    evaluateLegendOptional(expressions.bic, bic, setBic, 'modal-contact-update-legend-bic-valid')
  }

  return (
    <div className="form">
      <div id="modal-contact-update-container" className="scroll-y d-flex flex-column">
        <Accordion defaultActiveKey="0" flush>
          <Accordion.Item eventKey="0">
            <Accordion.Header bsPrefix='border'>
              <span className="fw-boldest fs-5">{lang.labels.contactIdentity}</span>
            </Accordion.Header>
            <Accordion.Body>
              { contact.type === "person" &&
                <>
                  <div className="fv-row mb-3">
                    <label className="form-label required">{lang.labels.firstName}</label>
                    <input id="modal-contact-update-input-first-name" className={`form-control ${contact.type === "person" && contact.data.uuid.length > 0 && "is-valid"}`} type="text" autoComplete="off" name="first-name" disabled={contact.type === "person" && contact.data.uuid.length > 0} placeholder={contact.data.first_name} value={firstName.value} onChange={handleChangeFirstName} onKeyUp={validateFirstName} onBlur={validateFirstName} />
                    <AppLegend component={"modal-contact-update"} attribute={{check: firstName.valid, name: "first-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mb-3">
                    <label className="form-label required">{lang.labels.lastName}</label>
                    <input id="modal-contact-update-input-last-name" className={`form-control ${contact.type === "person" && contact.data.uuid.length > 0 && "is-valid"}`} type="text" autoComplete="off" name="last-name" disabled={contact.type === "person" && contact.data.uuid.length > 0} placeholder={contact.data.last_name} value={lastName.value} onChange={handleChangeLastName} onKeyUp={validateLastName} onBlur={validateLastName} />
                    <AppLegend component={"modal-contact-update"} attribute={{check: lastName.valid, name: "last-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </>
              }
              { contact.type === "company" &&
                <>
                  <div className="fv-row mb-3">
                    <label className="form-label required">{lang.labels.companyName}</label>
                    <input id="modal-contact-update-input-name" className={`form-control ${contact.type === "company" && contact.data.uuid.length > 0 && "is-valid"}`} type="text" autoComplete="off" name="name" disabled={contact.type === "company" && contact.data.uuid.length > 0} placeholder={contact.data.name} value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                    <AppLegend component={"modal-contact-update"} attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mb-7">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.companyLogo}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title= {lang.labels.theFileMustBeFormat}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <div className="mt-5">
                      <AppInputImage image={logo} setImage={setLogo}></AppInputImage>
                    </div>
                  </div>
                </>
              }
              <div className="fv-row mb-3">
                <label className="form-label">{lang.labels.email}</label>
                <input id="modal-contact-update-input-email" className={`form-control ${contact.type === "person" && contact.data.uuid.length > 0 && "is-valid"}`} type="email" autoComplete="off" name="email" disabled={contact.type === "person" && contact.data.uuid.length > 0} placeholder={contact.data.email} value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
                <AppLegend component={"modal-contact-update"} attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">{lang.labels.phoneNumber}</label>
                <input id="modal-contact-update-input-phone" className="form-control" type="tel" autoComplete="off" name="phone" placeholder={contact.data.phone} value={phone.value} onChange={handleChangePhone} onKeyUp={validatePhone} onBlur={validatePhone} />
                <AppLegend component={"modal-contact-update"} attribute={{check: phone.valid, name: "phone", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">{lang.labels.webSite}</label>
                <input id="modal-contact-update-input-website" className="form-control" type="url" autoComplete="off" name="website" placeholder={contact.data.website} value={website.value} onChange={handleChangeWebsite} onKeyUp={validateWebsite} onBlur={validateWebsite} />
                <AppLegend component={"modal-contact-update"} attribute={{check: website.valid, name: "website", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              { contact.type === "company" &&
                <>
                  <div className="fv-row mb-3">
                    <label className="form-label">{lang.labels.registerNumber}</label>
                    <input id="modal-contact-update-input-register-number" className="form-control" type="text" autoComplete="off" name="register-number" placeholder={contact.data.register_number} value={registerNumber.value} onChange={handleChangeRegisterNumber} onKeyUp={validateRegisterNumber} onBlur={validateRegisterNumber} />
                    <AppLegend component={"modal-contact-update"} attribute={{check: registerNumber.valid, name: "register-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mb-3">
                    <label className="form-label">{lang.labels.vatNumber}</label>
                    <input id="modal-contact-update-input-vat-number" className="form-control"  type="text" autoComplete="off" name="vat-number" placeholder={contact.data.vat_number} value={vatNumber.value} onChange={handleChangeVatNumber} onKeyUp={validateVatNumber} onBlur={validateVatNumber} />
                    <AppLegend component={"modal-contact-update"} attribute={{check: vatNumber.valid, name: "vat-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                </>
              }
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header bsPrefix='border'>
              <span className="fw-boldest fs-5">{lang.labels.contactInformation}</span>
            </Accordion.Header>
            <Accordion.Body>
              <div className="row mx-0">
                <div className="col-md-8 fv-row mb-3 px-0 pe-md-2">
                  <label className="form-label">{lang.labels.street}</label>
                  <input id="modal-contact-update-input-street" className="form-control" type="text" autoComplete="off" name="street" placeholder={contact.data.address.street} value={street.value} onChange={handleChangeStreet} onKeyUp={validateStreet} onBlur={validateStreet} />
                  <AppLegend component={"modal-contact-update"} attribute={{check: street.valid, name: "street", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="col-md-4 fv-row mb-3 px-2 ps-md-0">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      {lang.labels.number}
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.whenNumberIsNotEntered}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <input id="modal-contact-update-input-street-number" className="form-control" type="text" autoComplete="off" name="number" placeholder={contact.data.address.number} value={streetNumber.value} onChange={handleChangeStreetNumber} onKeyUp={validateStreetNumber} onBlur={validateStreetNumber} />
                  <AppLegend component={"modal-contact-update"} attribute={{check: streetNumber.valid, name: "street-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.additional}
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifNecessaryEnterSomethingReferentialRegardingAddress}><FaExclamationCircle /></i>
                  </div>
                </label>
                <input id="modal-contact-update-input-additional" className="form-control" type="text" autoComplete="off" name="additional" placeholder={contact.data.address.additional} value={additional.value} onChange={handleChangeAdditional} onKeyUp={validateAdditional} onBlur={validateAdditional} />
                <AppLegend component={"modal-contact-update"} attribute={{check: additional.valid, name: "additional", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">{lang.labels.administrativeArea}</label>
                {country.value === 'CH'
                  ? <AppInputCanton component={"modal-contact-update"} canton={administrativeArea} setCanton={setAdministrativeArea} options={{disabled: false}} />
                  : <>
                      <input id="modal-contact-update-input-administrative-area" className="form-control" type="text" autoComplete="off" name="administrative-area" placeholder={contact.data.address.administrative_area} value={administrativeArea.value} onChange={handleChangeAdministrativeArea} onKeyUp={validateAdministrativeArea} onBlur={validateAdministrativeArea} />
                      <AppLegend component={"modal-contact-update"} attribute={{check: administrativeArea.valid, name: "administrative-area", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </>
                }
              </div>
              <div className="row mx-0">
                <div className="col-md-4 fv-row mb-3 px-0 pe-md-2">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      {lang.labels.zipCode}
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterThePostalCodeOfTheCity}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <input id="modal-contact-update-input-zip-code" className="form-control" type="text" autoComplete="off" name="zip-code" placeholder={contact.data.address.zip_code} value={zipCode.value} onChange={handleChangeZipCode} onKeyUp={validateZipCode} onBlur={validateZipCode} />
                  <AppLegend component={"modal-contact-update"} attribute={{check: zipCode.valid, name: "zip-code", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="col-md-8 fv-row mb-3 px-0 ps-md-2">
                  <label className="form-label">{lang.labels.city}</label>
                  <input id="modal-contact-update-input-city" className="form-control" type="text" autoComplete="off" name="city" placeholder={contact.data.address.city} value={city.value} onChange={handleChangeCity} onKeyUp={validateCity} onBlur={validateCity} />
                  <AppLegend component={"modal-contact-update"} attribute={{check: city.valid, name: "city", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">{lang.labels.country}</label>
                <AppInputCountry component={"modal-contact-update"} options={{small: false, disabled: false, placeholder: null}} country={country} setCountry={setCountry}></AppInputCountry>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header bsPrefix='border'>
              <span className="fw-boldest fs-5">{lang.labels.legalInformation}</span>
            </Accordion.Header>
            <Accordion.Body>
              { contact.type === "company" &&
                <div className="fv-row mb-3">
                  <label className="form-label">{lang.labels.currency}</label>
                  <AppInputCurrency component={"modal-contact-update"} currency={currency} setCurrency={setCurrency}></AppInputCurrency>
                </div>
              }
              <div className="fv-row mb-3">
                <label className="form-label">{lang.labels.financialEntity}</label>
                <div className="position-relative">
                  <input className="form-control pe-13" type="text" autoComplete="off" name="account-name" placeholder={contact.data.bank_account.name} value={financialEntity.value} onChange={handleChangeAccountName} onKeyUp={validateFinancialEntity} onBlur={validateFinancialEntity} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconBank} alt={lang.labels.bank} className="h-20px" />
                  </div>
                </div>
                <AppLegend component={"modal-contact-update"} attribute={{check: financialEntity.valid, name: "financial-entity", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.iban}
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumber}><FaExclamationCircle /></i>
                  </div>
                </label>
                <div className="position-relative">
                  <input id="modal-contact-update-input-iban" className="form-control pe-13" type="text" autoComplete="off" name="iban" placeholder={formatIBAN(lang, contact.data.bank_account.iban)} value={iban.value} onChange={handleChangeIban} onKeyUp={validateIban} onBlur={validateIban} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                  </div>  
                </div>
                <AppLegend component={"modal-contact-update"} attribute={{check: iban.valid, name: "iban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.bban}
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.basicBankAccountNumber}><FaExclamationCircle /></i>
                  </div>
                </label>
                <div className="position-relative">
                  <input id="modal-contact-update-input-bban" className="form-control pe-13" type="text" autoComplete="off" name="bban" placeholder={contact.data.bank_account.bban} value={bban.value} onChange={handleChangeBban} onKeyUp={validateBban} onBlur={validateBban} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                  </div>
                </div>
                <AppLegend component={"modal-contact-update"} attribute={{check: bban.valid, name: "bban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.bicSwift}
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.bankIdentifierCode}><FaExclamationCircle /></i>
                  </div>
                </label>
                <div className="position-relative">
                  <input id="modal-contact-update-input-bic" className="form-control pe-13" type="text" autoComplete="off" name="bic" placeholder={formatBIC(lang, contact.data.bank_account.bic)} value={bic.value} onChange={handleChangeBic} onKeyUp={validateBic} onBlur={validateBic} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                  </div>
                </div>
                <AppLegend component={"modal-contact-update"} attribute={{check: bic.valid, name: "bic", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="3">
            <Accordion.Header bsPrefix='border'>
              <span className="fw-boldest fs-5">{lang.labels.fileDownloadSettings}</span>
            </Accordion.Header>
            <Accordion.Body>
              <div className="fv-row mb-3">
                <label className="form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.language}
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.languageToDownload}><FaExclamationCircle /></i>
                  </div>
                </label>
                <AppInputLanguage component={"modal-contact-update"} language={language} setLanguage={setLanguage}></AppInputLanguage>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label">
                  <div className="d-flex align-items-center">
                    <span>{lang.labels.qrType}</span>
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.shapesOrVersionsOfQR}><FaExclamationCircle /></i>
                  </div>
                </label>
                <AppInputQrType component={"modal-contact-update"} qrType={qrType} setQrType={setQrType}></AppInputQrType>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  )
};

export default AppContactFormAccordion;
